import type { ReactElement } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { useInstructionProjects } from '@/hooks/api/useInstructionProjects';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { InventoryUserListDialog } from './InventoryUserListDialog';

interface InstructionProjectListModalProps {
  organizationId: string;
  contentId: string | null;
  /** Category scope for the list (KIR-3356): `undefined`/omitted = no scope
   * (the legacy content-scoped table), `null` = the untyped row, string = that
   * category. The live DataTable always passes the clicked row's category. */
  fileCategory?: string | null;
  onClose: () => void;
}

export function InstructionProjectListModal({
  organizationId,
  contentId,
  fileCategory,
  onClose,
}: InstructionProjectListModalProps): ReactElement {
  const { data, isLoading, error } = useInstructionProjects(
    organizationId,
    contentId,
    fileCategory
  );

  const projects = data?.projects ?? [];

  return (
    <InventoryUserListDialog
      open={contentId !== null}
      onClose={onClose}
      title={`Projects using this Instruction (${isLoading ? '...' : projects.length})`}
      description="Projects where this instruction was seen."
      columns={[
        { label: 'Project' },
        { label: 'Users', className: 'text-center' },
        { label: 'Last Seen' },
      ]}
      isLoading={isLoading}
      error={error}
      isEmpty={projects.length === 0}
      emptyMessage="No projects found"
      loadingMessage="Loading projects..."
      errorMessage="Failed to load projects"
    >
      {projects.map(project => (
        <TableRow key={project.projectId}>
          <TableCell className="font-medium break-all">
            {project.name}
          </TableCell>
          <TableCell className="text-center">{project.userCount}</TableCell>
          <TableCell>
            <RelativeTimeWithTooltip date={project.lastSeenAt} />
          </TableCell>
        </TableRow>
      ))}
    </InventoryUserListDialog>
  );
}
